import { useEffect, useCallback, useRef } from 'react';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { useCompanyId, useDivisionId } from 'src/utils/Hooks/reduxSelectors';
import { useIntegrationStore } from './integrationStore';

const OAUTH_CALLBACK_URL = 'https://beacon.stackline.com/api/integration/salsify/oauth2callback';

// URL parameter key for OAuth callback success
const SYNDICATION_SUCCESS_PARAM = 'syndicationSuccess';

export interface UseSalsifyIntegrationResult {
  isLoading: boolean;
  isConnected: boolean;
  needsReconnect: boolean;
  handleConnect: () => void;
  refetch: () => Promise<void>;
}

/**
 * Removes integration-related query parameters from the URL without triggering a page reload.
 */
const cleanupIntegrationParams = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete(SYNDICATION_SUCCESS_PARAM);
  window.history.replaceState({}, '', url.toString());
};

/**
 * Hook to manage Salsify integration status and OAuth connection flow.
 * Uses the singleton integrationStore for caching status across the app.
 */
export const useSalsifyIntegration = (): UseSalsifyIntegrationResult => {
  const { showSnackbar } = useSnackbar();
  const { targetUrl } = useAppSelector((state) => state.app);
  const companyId = useCompanyId();
  const divisionId = useDivisionId();

  // Get state and actions from the Zustand store
  const providerState = useIntegrationStore((state) => state.providers.salsify);
  const fetchProviderStatus = useIntegrationStore((state) => state.fetchProviderStatus);

  // Track if we've already shown the success snackbar to prevent duplicates
  const hasShownSuccessSnackbar = useRef(false);

  const refetch = useCallback(async () => {
    // Force refetch by resetting loading state first
    useIntegrationStore.getState().setProviderLoading('salsify', true);
    useIntegrationStore.getState().setProviderStatus('salsify', null);
    await fetchProviderStatus('salsify', targetUrl);
  }, [fetchProviderStatus, targetUrl]);

  // Check for OAuth callback success and show snackbar
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const syndicationSuccess = urlParams.get(SYNDICATION_SUCCESS_PARAM);

    // Only process if syndicationSuccess=true and haven't shown snackbar yet
    if (syndicationSuccess === 'true' && !hasShownSuccessSnackbar.current) {
      hasShownSuccessSnackbar.current = true;

      showSnackbar({
        type: 'success',
        message: "You've successfully connected the Salsify syndication integration."
      });

      // Refetch to get updated status after successful connection
      refetch();

      // Clean up URL parameters
      cleanupIntegrationParams();
    }
  }, [showSnackbar, refetch]);

  // Fetch status on mount if not already loaded
  useEffect(() => {
    if (targetUrl) {
      fetchProviderStatus('salsify', targetUrl);
    }
  }, [fetchProviderStatus, targetUrl]);

  const status = providerState.status;
  const isLoading = providerState.isLoading;

  // Simplified status: isConnected means fully connected with valid token
  const isConnected = status?.isConnected ?? false;
  // needsReconnect is directly tied to isTokenValid (derived in store)
  const needsReconnect = status?.needsReconnect ?? false;
  // clientId is only needed internally for OAuth flow
  const clientId = status?.clientId ?? null;

  const handleConnect = useCallback(() => {
    if (!companyId || !divisionId) {
      showSnackbar({ type: 'error', message: 'Unable to get company or division information' });
      return;
    }

    if (!clientId) {
      showSnackbar({ type: 'error', message: 'Salsify credentials are not configured. Please contact support.' });
      return;
    }

    // Build clean redirect URL without any existing integration params
    const redirectUrl = new URL(window.location.href);
    redirectUrl.searchParams.delete(SYNDICATION_SUCCESS_PARAM);

    // Create the state parameter with company and division info
    const state = {
      companyId,
      divisionId,
      redirectUrl: redirectUrl.toString()
    };

    const encodedState = btoa(JSON.stringify(state));

    // Redirect to Salsify OAuth using the clientId from Secrets Manager
    const salsifyOAuthUrl = `https://app.salsify.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(OAUTH_CALLBACK_URL)}&response_type=code&state=${encodedState}`;

    window.location.href = salsifyOAuthUrl;
  }, [companyId, divisionId, clientId, showSnackbar]);

  return {
    isLoading,
    isConnected,
    needsReconnect,
    handleConnect,
    refetch
  };
};
